import { cellAtWorld, cellCenter, heroWalkable } from '../grid.ts';
import { dist2, imax, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { BPt, BookSealRules, SealsState } from '../biblio-types.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { sealOrder, spokeState } from '../battle/biblio.ts';
import { useSkills } from './ghost.ts';

/**
 * The lab's bot for the mage's chapter 1 (docs/sendas/mago.md, «Ceniza»; the common bot in ai/senda-bot.ts hands these
 * acts here). It only moves the hero, picks its target and taps marks, as a player's commands would.
 *
 * - the cellars (`soot`): who knows shoots the pages first, puts down what rose in its hall from where it stands, and
 *   goes on down; who doesn't walks on to the catalog, shooting what is near on the way.
 * - the locks and the cabinet (`seals`): who knows breaks the frost seal with its bowl and then a bolt, the crust seal
 *   with its flask and then its brazier (never a bolt on the acid), and leaves the ink for a scribe: it wounds one on
 *   its way in and kills it while it writes; the pages near it first; off a wall of fire warned under it. Who doesn't
 *   bolts each seal, breaks the things by it as it finds them (a bolt on the seal after each), and kills every scribe
 *   on sight; it steps off the red floor too (anyone sees it).
 */

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

/** Shoots `t` (the skills too); `chase`: walks after it. */
const strike = (s: BattleState, h: Hero, t: Enemy, chase = true): void => {
  useSkills(s, h, t);
  h.target = t.id;
  h.forced = chase;
};

/* ---------- the cellars ---------- */

const sootBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.soot;
  const w = s.senda?.soot;
  if (!r || !w) return;
  const near = enemiesNear(s, h.x, h.y, knows ? 5200 : 2600, (e) => targetable(s, e));
  if (knows) {
    const page = nearestTo(
      near.filter((e) => e.kind === 'crow'),
      h.x,
      h.y,
    );
    const t = page ?? nearestTo(near, h.x, h.y);
    if (t) return strike(s, h, t, false);
  } else {
    const t = nearestTo(near, h.x, h.y);
    if (t) return strike(s, h, t, false);
  }
  // On down: this hall's stair (its beams up), or the catalog.
  const hl = r.halls[w.hall];
  if (!hl) return;
  const last = w.hall >= r.halls.length - 1;
  if (last) return go(s, h, r.catalog.x, r.catalog.y);
  const c = cellCenter(s.map.radius, hl.stair[0] as number);
  // Beams down: what rose here is still up somewhere; go for the nearest nightmare at all.
  if (w.shut[w.hall]) {
    const any = nearestTo(
      s.enemies.filter((e) => e.hp > 0 && targetable(s, e)),
      h.x,
      h.y,
    );
    if (any) return strike(s, h, any);
  }
  const below = r.halls[w.hall + 1];
  go(s, h, below ? (c.x + below.at.x) >> 1 : c.x, below ? c.y + 1800 : c.y);
};

/* ---------- the locks ---------- */

/** The cells of the walls of fire warned or up now. */
const hotCells = (s: BattleState, r: BookSealRules): Set<number> => {
  const hot = new Set<number>();
  const sp = r.spokes;
  if (!sp) return hot;
  for (let k = 0; k < sp.cells.length; k++) if (spokeState(sp, k, s.waveT) > 0) for (const c of sp.cells[k] as number[]) hot.add(c);
  return hot;
};

/** A spoke warned or up under the hero (off it now). */
const onFire = (s: BattleState, h: Hero, r: BookSealRules): boolean => hotCells(s, r).has(cellAtWorld(s, h.x, h.y));

/** About to step onto red floor: it stops short and waits (the wall rises, the way round opens, or it goes down). */
const intoFire = (s: BattleState, h: Hero, r: BookSealRules): boolean => {
  if (!r.spokes || !h.moving) return false;
  const hot = hotCells(s, r);
  const next = h.path[0];
  if (next === undefined || !hot.has(next)) return false;
  h.moving = false;
  h.path = [];
  h.target = 0;
  return true;
};

/** Steps off the red floor: to the nearest cell no wall warned or up is on. */
const offFire = (s: BattleState, h: Hero, r: BookSealRules): void => {
  const sp = r.spokes;
  if (!sp) return;
  const hot = hotCells(s, r);
  let best: BPt | null = null;
  let bd = 0x7fffffff;
  for (let dx = -2000; dx <= 2000; dx += 500)
    for (let dy = -2000; dy <= 2000; dy += 500) {
      const id = cellAtWorld(s, h.x + dx, h.y + dy);
      if (id < 0 || hot.has(id) || !heroWalkable(s, id)) continue;
      const p = cellCenter(s.map.radius, id);
      const d = dist2(p.x, p.y, h.x, h.y);
      if (d < bd) {
        bd = d;
        best = p;
      }
    }
  if (best) {
    const w = s.senda?.seals;
    if (w) w.job = -1;
    h.target = 0;
    go(s, h, best.x, best.y, 100);
  }
};

/** The item of a kind by seal `i` that's there (−1 none). */
const itemFor = (r: BookSealRules, w: SealsState, i: number, kind: number): number => r.items.findIndex((it, k) => it.seal === i && it.kind === kind && w.back[k] === 0);

/** The mark that moves seal `i` on, by the rule (−1: nothing to do now, wait for what's coming back, or a scribe). */
const rightStep = (r: BookSealRules, w: SealsState, i: number): number => {
  const sl = w.seals[i];
  const def = r.seals[i];
  if (!sl || !def || sl.broken) return -1;
  if (def.kind === 0) return sl.prep === 1 ? r.items.length + i : itemFor(r, w, i, 0);
  if (def.kind === 1) return sl.prep === 2 ? itemFor(r, w, i, 2) : itemFor(r, w, i, 1);
  return -1;
};

/** The blind bot's tries: by seal, the items it means to break in turn, and whether the next tap is the seal. */
const BLIND = new WeakMap<BattleState, { order: number[]; k: number; bolt: boolean }>();

const blindStep = (s: BattleState, r: BookSealRules, w: SealsState): number => {
  let b = BLIND.get(s);
  if (!b) {
    // The seals in the room's order, and by each the things in a random order.
    const order: number[] = [];
    for (let i = 0; i < r.seals.length; i++) {
      const its = r.items.map((it, k) => ({ it, k })).filter((o) => o.it.seal === i);
      while (its.length) order.push((its.splice(rngInt(s.rng, its.length), 1)[0] as { k: number }).k);
    }
    b = { order, k: 0, bolt: true };
    BLIND.set(s, b);
  }
  // A bolt at the seal of the next thing (to see what it does), then the thing, then the seal again.
  for (let n = 0; n < b.order.length; n++) {
    const k = b.order[(b.k + n) % b.order.length] as number;
    const it = r.items[k] as BookSealRules['items'][number];
    if (w.seals[it.seal]?.broken) continue;
    b.k = (b.k + n) % b.order.length;
    if (b.bolt) {
      b.bolt = false;
      return r.items.length + it.seal;
    }
    if (w.back[k]) return r.items.length + it.seal;
    b.bolt = true;
    b.k = (b.k + 1) % b.order.length;
    return k;
  }
  return -1;
};

const sealsBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.seals;
  const w = s.senda?.seals;
  if (!r || !w || w.done >= 0) return;
  if (onFire(s, h, r)) return offFire(s, h, r);
  if (intoFire(s, h, r)) return;
  // (No chasing across the room: a target out of reach is walked to, the red floor stopped short of.)
  const strike = (s0: BattleState, h0: Hero, t: Enemy, _chase = true): void => {
    useSkills(s0, h0, t);
    if (withinRange(h0.x, h0.y, t.x, t.y, h0.stats.range)) {
      h0.target = t.id;
      h0.forced = false;
    } else go(s0, h0, t.x, t.y, 900);
  };
  // The scribes: who knows wounds one on its way in (down to a third) and kills it as it writes; who doesn't, on sight.
  const scribes = w.scribes.map((u) => ({ u, e: findEnemy(s, u.id) })).filter((o): o is { u: SealsState['scribes'][number]; e: Enemy } => !!o.e && o.e.hp > 0);
  const inkLeft = r.seals.some((x, i) => x.kind === 2 && !w.seals[i]?.broken);
  const others = r.seals.every((x, i) => x.kind === 2 || w.seals[i]?.broken);
  const writing = scribes.find((o) => o.u.mode === 1);
  // Who doesn't know puts every scribe down the moment it shows (it seals: that's all it knows).
  if (!knows && scribes[0]) {
    w.job = -1;
    return strike(
      s,
      h,
      nearestTo(
        scribes.map((o) => o.e),
        h.x,
        h.y,
      ) as Enemy,
    );
  }
  // Pages at it: off first, but for a scribe writing (a job half walked is let go).
  const pages = enemiesNear(s, h.x, h.y, knows ? 2600 : 2000, (e) => e.kind === 'crow' && targetable(s, e));
  if (pages.length >= 2 && !(knows && writing)) {
    w.job = -1;
    return strike(s, h, nearestTo(pages, h.x, h.y) as Enemy, false);
  }
  if (knows) {
    if (writing && (inkLeft || w.seals.some((x) => x.broken))) {
      w.job = -1;
      return strike(s, h, writing.e);
    }
    // (Down to a fifth on its way in: it dies in the writing. With the ink broken, put down outright.)
    const coming = scribes.find((o) => o.u.mode !== 1 && o.e.hp > mulBp(o.e.maxHp, inkLeft ? 2000 : 0));
    if (coming) {
      w.job = -1;
      return strike(s, h, coming.e);
    }
  }
  if (pages.length && w.job < 0) {
    w.job = -1;
    return strike(s, h, nearestTo(pages, h.x, h.y) as Enemy, false);
  }
  if (w.job >= 0) return;
  let at = -1;
  if (knows) {
    // (In the test a seal broken alone closes again: who knows readies both early and breaks them once a scribe is
    // coming to write, so the ink follows while they still hold.)
    const timed = !r.hold || scribes.length > 0 || r.seals.some((x, i) => x.kind === 2 && w.seals[i]?.broken);
    for (let i = 0; i < r.seals.length && at < 0; i++) {
      const step = rightStep(r, w, i);
      const breaks = step >= 0 && w.seals[i] && ((r.seals[i]?.kind === 0 && w.seals[i]!.prep === 1) || (r.seals[i]?.kind === 1 && w.seals[i]!.prep === 2));
      if (step >= 0 && (!breaks || timed)) at = step;
    }
    // All but the ink broken, no scribe yet: keep near the desk, shooting what comes.
    if (at < 0 && others) {
      const t = nearestTo(
        enemiesNear(s, h.x, h.y, h.stats.range, (e) => targetable(s, e) && e.kind !== 'scribe'),
        h.x,
        h.y,
      );
      if (t) return strike(s, h, t, false);
      const p = r.desk;
      if (!withinRange(h.x, h.y, p.x, p.y, imax(2500, h.stats.range - 1500))) go(s, h, p.x + (h.x > p.x ? 2500 : -2500), p.y + 1500);
      return;
    }
  } else at = blindStep(s, r, w);
  if (at >= 0) {
    sealOrder(s, h, at);
    return;
  }
  const t = nearestTo(
    enemiesNear(s, h.x, h.y, h.stats.range, (e) => targetable(s, e)),
    h.x,
    h.y,
  );
  if (t) strike(s, h, t, false);
};

/** The library's acts, if this is one (true: handled). */
export const magoBot = (s: BattleState, h: Hero, knows: boolean): boolean => {
  const r = s.rules?.senda;
  if (r?.soot) {
    sootBot(s, h, knows);
    return true;
  }
  if (r?.seals) {
    sealsBot(s, h, knows);
    return true;
  }
  return false;
};
